import type {
  DeploymentResponse,
  GetV1WorkspacesWorkspaceidFleetsFleetidDeploymentsQueryParams,
  PostV1WorkspacesWorkspaceidFleetsFleetidDeploymentsMutationRequest,
} from '@emma/api_client/types';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ReadToastOptions, useApiFetch, useApiMutation } from '../api';

export function useDeployments(
  workspaceId: number,
  fleetId: number,
  params: GetV1WorkspacesWorkspaceidFleetsFleetidDeploymentsQueryParams = {},
  toastOptions?: ReadToastOptions
) {
  return useApiFetch(
    `workspaces/${workspaceId}/fleets/${fleetId}/deployments?${JSON.stringify(params)}`,
    async (callApi) =>
      await callApi((api) =>
        api.fleets.getV1WorkspacesWorkspaceidFleetsFleetidDeployments(
          { workspaceId, fleetId },
          params
        )
      ),
    { keepPreviousData: true, ...toastOptions }
  );
}

export function useCreateDeployment(
  workspaceId: number,
  fleetId: number,
  actionOnErr: (error?: unknown) => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/fleets/${fleetId}/deployments`;
  const fleetCacheKey = `workspaces/${workspaceId}/fleets/${fleetId}`;

  const createDeploymentMutation = useApiMutation<
    DeploymentResponse,
    PostV1WorkspacesWorkspaceidFleetsFleetidDeploymentsMutationRequest
  >(
    listCacheKey,
    async (callApi, data) => {
      const response = await callApi((api) =>
        api.fleets.postV1WorkspacesWorkspaceidFleetsFleetidDeployments(
          { workspaceId, fleetId },
          data
        )
      );
      return response.data;
    },
    actionOnErr,
    true
  );

  return useCallback(
    async (data: PostV1WorkspacesWorkspaceidFleetsFleetidDeploymentsMutationRequest) => {
      const result = await createDeploymentMutation(data);
      await mutate(
        (key: unknown) =>
          typeof key === 'string' && (key === listCacheKey || key.startsWith(`${listCacheKey}?`))
      );
      await mutate(fleetCacheKey);
      return result;
    },
    [createDeploymentMutation, mutate, listCacheKey, fleetCacheKey]
  );
}
